import { useState, useEffect, useCallback } from 'react';
import { SettingsNativeBridgeFactory } from '../bridge/SettingsNativeBridgeFactory';
import { GetSettingsResponse } from '../Contracts/responses';
import { SettingsLogger, logConstants, AotCategory } from '../utils/SettingsLogger';
import {
	getFilteredSettingIds,
	expandGroupSettingIds,
} from '../config/DynamicSettingsFilter';
import { SettingsCategory } from '../config/SettingsCategory';
import { ISettingCategory } from '../config/SettingsCategories';
import { settingsCache } from '../cache/SettingsCache';

interface UseCategoryVisibilityResult {
	visibleCategories: ISettingCategory[];
	isLoading: boolean;
	error: string | null;
}

/**
 * Hook to determine which settings categories should be visible in the navigation.
 * A category is hidden if ALL of its settings have hidden: true (GPO/GACS).
 *
 * Performance: all category fetches run in parallel (Promise.all) and each result
 * is stored in settingsCache so that useSettingsPage can serve subsequent page loads
 * from the cache without additional bridge calls.
 */
export const useCategoryVisibility = (
	categories: ISettingCategory[]
): UseCategoryVisibilityResult => {
	const [visibleCategories, setVisibleCategories] = useState<ISettingCategory[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const checkCategoryVisibility = useCallback(async () => {
		try {
			setIsLoading(true);
			const bridge = SettingsNativeBridgeFactory.getBridge();
			const platform = bridge.getPlatform();
			const entrypoint = bridge.getEntrypoint();

			// Build work items only for categories that have settings on this platform
			const workItems = categories
				.map((category) => {
					const settingIds = getFilteredSettingIds({
						category: category.id as SettingsCategory,
						platform,
						entrypoint,
					});
					const fetchIds = expandGroupSettingIds(settingIds);
					return { category, settingIds, fetchIds };
				})
				.filter((item) => item.settingIds.length > 0);

			// Fetch all categories in parallel — single concurrent round-trip to the bridge
			const responses = await Promise.all(
				workItems.map(
					(item) =>
						bridge
							.getSettings({
								settings: item.fetchIds.map((settingKey) => ({ settingKey })),
							})
							.catch(() => null) // treat per-category errors as no-response (show category)
				)
			);

			const categoriesWithVisibility: ISettingCategory[] = [];

			responses.forEach((settingsResponse, i) => {
				const { category } = workItems[i];

				if (settingsResponse && settingsResponse.status === 0) {
					const getSettingsResp = settingsResponse as GetSettingsResponse;
					const settingsArray = getSettingsResp.data?.settings || [];

					// Populate cache so useSettingsPage can skip its own bridge call
					settingsCache.set(category.id, settingsArray);

					const hasVisibleSettings = settingsArray.some(
						(setting) => setting.hidden !== true
					);

					if (hasVisibleSettings) {
						categoriesWithVisibility.push(category);
					} else {
						SettingsLogger.getInstance().write(
							logConstants.INFO,
							`Category '${category.id}' hidden: all settings are hidden by GPO/GACS`,
							AotCategory.Info,
							`Category '${category.id}' hidden: all settings are hidden by GPO/GACS`
						);
					}
				} else {
					// If we can't fetch settings, show the category anyway
					categoriesWithVisibility.push(category);
				}
			});

			// Sort categories by their order property to ensure consistent ordering
			categoriesWithVisibility.sort((a, b) => (a.order || 0) - (b.order || 0));

			setVisibleCategories(categoriesWithVisibility);
			setIsLoading(false);
		} catch (err) {
			const errorMessage = `Failed to check category visibility: ${err}`;
			SettingsLogger.getInstance().write(
				logConstants.ERROR,
				errorMessage,
				AotCategory.Error,
				errorMessage
			);
			setError(errorMessage);
			// On error, show all categories
			setVisibleCategories(categories);
			setIsLoading(false);
		}
	}, [categories]);

	useEffect(() => {
		if (categories.length > 0) {
			checkCategoryVisibility();
		} else {
			setVisibleCategories([]);
			setIsLoading(false);
		}
	}, [categories, checkCategoryVisibility]);

	return {
		visibleCategories,
		isLoading,
		error,
	};
};
